import React, { useState, useRef, useEffect } from 'react';

const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholder = null,
  fallback = null,
  onError = null,
  videoId = null, // For YouTube thumbnails
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
  const imgRef = useRef(null);
  
  // Get progressive image sources for YouTube thumbnails
  // Start with medium quality, then try high quality
  const imageSources = videoId 
    ? [
        `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,     // High quality (480x360)
        `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,     // Medium quality
        `https://img.youtube.com/vi/${videoId}/default.jpg`        // Default quality
      ] 
    : [src];
  const currentSrc = imageSources[currentSrcIndex];

  useEffect(() => {
    // For images that should load immediately (above-the-fold), skip intersection observer
    if (placeholder === 'immediate') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '100px' // Start loading 100px before the image comes into view
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [placeholder]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    // Try next quality level if available
    if (currentSrcIndex < imageSources.length - 1) {
      setCurrentSrcIndex(prev => prev + 1);
    } else {
      setHasError(true);
      onError?.();
    }
  };

  return (
    <div ref={imgRef} className={`relative ${className}`} {...props}>
      {/* Placeholder/Loading state */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center">
          {placeholder || (
            <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin"></div>
          )}
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
          {fallback || (
            <div className="text-white/40 text-center">
              <div className="w-8 h-8 mx-auto mb-2">📷</div>
              <p className="text-xs">Image unavailable</p>
            </div>
          )}
        </div>
      )}

      {/* Actual image */}
      {isInView && (
        <img
          src={currentSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
        />
      )}
    </div>
  );
};

export default LazyImage;
